import { Suspense, lazy } from "react";
import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { AvisoCookies } from "./components/aviso-cookies";
import {
  FocoDeRuta,
  ID_CONTENIDO,
  SaltarAlContenido,
} from "./components/navegacion";
import { LoadingState } from "./components/states";
import Home from "./routes/home";
import NotFound from "./routes/not-found";

/*
 * Carga diferida por ruta: la pagina publica de un tenant no descarga el panel.
 *
 * La sesion no se monta aca: el proveedor esta arriba del router
 * (`main.tsx`) y recien pregunta por ella la primera pantalla que la usa.
 */
const SesionRequerida = lazy(() => import("./routes/area-privada"));
const TenantLanding = lazy(() => import("./routes/tenant-landing"));
const Booking = lazy(() => import("./routes/booking"));
const Reserva = lazy(() => import("./routes/reserva"));
const MisTurnos = lazy(() => import("./routes/mis-turnos"));
const SignInPage = lazy(() => import("./routes/auth/sign-in"));
const SignUpPage = lazy(() => import("./routes/auth/sign-up"));
const RecuperarPage = lazy(() => import("./routes/auth/recuperar"));
const Onboarding = lazy(() => import("./routes/onboarding"));
const Invitacion = lazy(() => import("./routes/invitacion"));
const TerminosPage = lazy(() =>
  import("./routes/legal").then((module) => ({ default: module.TerminosPage })),
);
const PrivacidadPage = lazy(() =>
  import("./routes/legal").then((module) => ({
    default: module.PrivacidadPage,
  })),
);
const PanelLayout = lazy(() => import("./routes/panel/layout"));
const Overview = lazy(() => import("./routes/panel/overview"));
const Agenda = lazy(() => import("./routes/panel/agenda"));
const Metrics = lazy(() => import("./routes/panel/metrics"));
const Services = lazy(() => import("./routes/panel/services"));
const Resources = lazy(() => import("./routes/panel/resources"));
const Team = lazy(() => import("./routes/panel/team"));
const Customers = lazy(() => import("./routes/panel/customers"));
const CustomerDetail = lazy(() => import("./routes/panel/customer"));
const Auditoria = lazy(() => import("./routes/panel/auditoria"));
const Accesos = lazy(() => import("./routes/panel/accesos"));
const SettingsPage = lazy(() => import("./routes/panel/settings"));
const Caja = lazy(() => import("./routes/panel/caja"));
const Vender = lazy(() => import("./routes/panel/caja-vender"));
const VentaDetalle = lazy(() => import("./routes/panel/caja-venta"));
const Productos = lazy(() => import("./routes/panel/productos"));
const Paquetes = lazy(() => import("./routes/panel/paquetes"));
const Facturas = lazy(() => import("./routes/panel/facturas"));
const FacturaDetalle = lazy(() =>
  import("./routes/panel/facturas").then((module) => ({ default: module.FacturaDetalle })),
);
const SubscriptionPage = lazy(() => import("./routes/panel/subscription"));
const AccountPage = lazy(() =>
  import("./routes/panel/account").then((module) => ({
    default: module.AccountPage,
  })),
);

function Loading(): ReactNode {
  return (
    <div className="mx-auto max-w-3xl px-4">
      <LoadingState />
    </div>
  );
}

export default function App(): ReactNode {
  return (
    <>
      <SaltarAlContenido />
      <FocoDeRuta />

      {/*
        El contenedor de la ruta. Existe para dos cosas que en una SPA no pasan
        solas: recibir el foco al navegar y ser el destino del salto al
        contenido. `tabIndex={-1}` lo hace enfocable por codigo sin meterlo en
        el recorrido del tabulador.
      */}
      <div id={ID_CONTENIDO} tabIndex={-1} className="outline-none">
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<Home />} />


            {/*
            Publicas: se leen antes de tener cuenta, desde el registro y desde el
            pie de la landing. Van antes de `/:tenantSlug` para que la ruta con
            nombre gane; el identificador de un negocio no puede ser ninguna de
            estas palabras (ver `RESERVED_SLUGS` en la API).
          */}
            <Route path="/terminos" element={<TerminosPage />} />
            <Route path="/privacidad" element={<PrivacidadPage />} />

            {/*
              Ingresar, registrarse y recuperar la contraseña: no exigen
              sesion, es justo donde se consigue.
            */}
            <Route path="/ingresar/recuperar" element={<RecuperarPage />} />
            <Route path="/ingresar/*" element={<SignInPage />} />
            <Route path="/registro/*" element={<SignUpPage />} />

            {/*
              La invitacion al panel: pregunta si hay sesion (para aceptar),
              pero no la exige — quien la abre puede no tener cuenta todavia.
              Ningun negocio puede llamarse "invitacion" (`RESERVED_SLUGS` en
              la API).
            */}
            <Route path="/invitacion/:token" element={<Invitacion />} />

            {/* Y esto ademas exige sesion iniciada. */}
            <Route element={<SesionRequerida />}>
              <Route path="/crear-negocio" element={<Onboarding />} />

              <Route path="/panel" element={<PanelLayout />}>
                <Route index element={<Overview />} />
                <Route path="agenda" element={<Agenda />} />
                <Route path="caja" element={<Caja />} />
                <Route path="caja/vender" element={<Vender />} />
                <Route path="caja/ventas/:id" element={<VentaDetalle />} />
                <Route path="productos" element={<Productos />} />
                <Route path="paquetes" element={<Paquetes />} />
                <Route path="facturacion" element={<Facturas />} />
                <Route path="facturacion/:id" element={<FacturaDetalle />} />
                <Route path="metricas" element={<Metrics />} />
                <Route path="servicios" element={<Services />} />
                <Route path="recursos" element={<Resources />} />
                <Route path="equipo" element={<Team />} />
                <Route path="clientes" element={<Customers />} />
                <Route path="clientes/:id" element={<CustomerDetail />} />
                <Route path="accesos" element={<Accesos />} />
                <Route path="auditoria" element={<Auditoria />} />
                <Route path="configuracion" element={<SettingsPage />} />
                <Route path="suscripcion" element={<SubscriptionPage />} />
                <Route path="cuenta" element={<AccountPage />} />
                {/* La organizacion de Clerk ya no existe: su gente esta en Accesos. */}
                <Route path="organizacion/*" element={<Navigate to="/panel/accesos" replace />} />
              </Route>
            </Route>

            {/* Compatibilidad con el enlace anterior. */}
            <Route
              path="/dashboard/*"
              element={<Navigate to="/panel" replace />}
            />

            {/*
            La reserva vista por quien la hizo, desde el enlace del correo.
            Va antes de `/:tenantSlug` por lo mismo que las de arriba: ningun
            negocio puede llamarse "reserva" (ver `RESERVED_SLUGS` en la API).
          */}
            <Route path="/reserva/:token" element={<Reserva />} />

            <Route path="/:tenantSlug" element={<TenantLanding />} />
            <Route path="/:tenantSlug/reservar" element={<Booking />} />
            <Route path="/:tenantSlug/mis-turnos" element={<MisTurnos />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>

      {/* Fuera del Suspense: no depende de la ruta ni de que termine de cargar. */}
      <AvisoCookies />
    </>
  );
}
